/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from "react";
import {MRT_ColumnDef} from "material-react-table";
import {Box, Tab, Tabs, TextField} from "@mui/material";
import {cloneDeep} from "lodash";
import { validateInput } from "./ParameterProperties";
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";


const MiscellaneousParameterProperties = (props: any) => {
  const [generalData, setGeneralData] = useState<any[]>([]);
  const [frontProfileData, setFrontProfileData] = useState<any[]>([]);
  const [sideProfileData, setSideProfileData] = useState<any[]>([]);
  const [rearProfileData, setRearProfileData] = useState<any[]>([]);
  const [topDownProfileData, setTopDownProfileData] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState("General Properties");
  const [editableCell, setEditableCell] = useState<{ rowIndex: number; columnId: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string | null }>({});
  const [temporaryValue, setTemporaryValue] = useState<string | number | null>(null);

  useEffect(() => {
    if (props.object && props.object.parameters) {
      const {general, frontProfile, sideProfile, rearProfile, topDownProfile} = props.object.parameters;

      const formatKey = (key: string) =>
        key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (str: string) => str.toUpperCase());

      // General data handling
      if (general) {
        const {comments} = general;

        const numericFields = [
          "width",
          "height",
          "length",
        ];

        const data = numericFields.map((field) => ({
          property: formatKey(field),
          value: general[field] !== "" && general[field] != null ? general[field] : null,
          units: "meters",
          dbKey: field,
        }));

        data.push(
          { property: "Comments", value: comments ?? "", units: "", dbKey: "comments" }
        );

        setGeneralData(data);
      }

      const desiredKeys = ["orientationWidth", "orientationHeight", "radarCrossSection", "retroreflectivity"];

      const mapProfileData = (profile: any) =>
        Object.entries(profile)
          .filter(([key]) => desiredKeys.includes(key))
          .map(([key, value]) => ({
            property: formatKey(key),
            value: value !== "" && value != null ? value : null,
            units: key === "radarCrossSection" ? "dBsm" : key === "retroreflectivity" ? "cd/lx/m2" : "meters",
            dbKey: key,
          }));

      // Similar logic for frontProfile, sideProfile, and rearProfile...
      if (frontProfile) {
        const _data = mapProfileData(frontProfile);
        _data.push({ property: "Comments", value: frontProfile.comments ?? "", units: "", dbKey: "comments" });
        _data.push({ property: "ID", value: frontProfile.id ?? "", units: "", dbKey: "id" });
        setFrontProfileData(_data);
      }

      if (sideProfile) {
        const _data = mapProfileData(sideProfile);
        _data.push({ property: "Comments", value: sideProfile.comments ?? "", units: "", dbKey: "comments" });
        _data.push({ property: "ID", value: sideProfile.id ?? "", units: "", dbKey: "id" });
        setSideProfileData(_data);
      }

      if (rearProfile) {
        const _data = mapProfileData(rearProfile);
        _data.push({ property: "Comments", value: rearProfile.comments ?? "", units: "", dbKey: "comments" });
        _data.push({ property: "ID", value: rearProfile.id ?? "", units: "", dbKey: "id" });
        setRearProfileData(_data);
      }

      if (topDownProfile) {
        const _data = mapProfileData(topDownProfile);
        _data.push({ property: "Comments", value: topDownProfile.comments ?? "", units: "", dbKey: "comments" });
        _data.push({ property: "ID", value: topDownProfile.id ?? "", units: "", dbKey: "id" });
        setTopDownProfileData(_data);
      }

    } else {
      setGeneralData([]);
      setFrontProfileData([]);
      setSideProfileData([]);
      setRearProfileData([]);
      setTopDownProfileData([]);
    }
  }, [props]);

  useEffect(() => {
    props.onDataUpdate({
      generalData,
      frontProfileData,
      sideProfileData,
      rearProfileData,
      topDownProfileData,
    })

    // props.object has the full object. we will send modified object here. need to use dbKey to update the object profiles
    const cpObj = cloneDeep(props.object)
    const { general, frontProfile, sideProfile, rearProfile, topDownProfile } = cpObj.parameters;
    if (general) {
      const generalDataMap = Object.fromEntries(generalData.map((item) => [item.dbKey, item.value]));
      Object.assign(general, generalDataMap);
    }
    if (frontProfile) {
      const frontDataMap = Object.fromEntries(frontProfileData.map((item) => [item.dbKey, item.value]));
      Object.assign(frontProfile, frontDataMap);
    }
    if (sideProfile) {
      const sideDataMap = Object.fromEntries(sideProfileData.map((item) => [item.dbKey, item.value]));
      Object.assign(sideProfile, sideDataMap);
    }
    if (rearProfile) {
      const rearDataMap = Object.fromEntries(rearProfileData.map((item) => [item.dbKey, item.value]));
      Object.assign(rearProfile, rearDataMap);
    }
    if (topDownProfile) {
      const topDownDataMap = Object.fromEntries(topDownProfileData.map((item) => [item.dbKey, item.value]));
      Object.assign(topDownProfile, topDownDataMap);
    }

    props.onDataUpdate(cpObj);
  }, [generalData, frontProfileData, sideProfileData, rearProfileData, topDownProfileData, props.object]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
    setEditableCell(null); // Clear editable cell on tab change
  };


  const handleCellClick = (rowIndex: number, columnId: string) => {
    // Get the property name based on the selected tab
    let rowProperty;
    switch (selectedTab) {
      case "General Properties":
        rowProperty = generalData[rowIndex]?.property;
        break;
      case "Front Profile":
        rowProperty = frontProfileData[rowIndex]?.property;
        break;
      case "Side Profile":
        rowProperty = sideProfileData[rowIndex]?.property;
        break;
      case "Rear Profile":
        rowProperty = rearProfileData[rowIndex]?.property;
        break;
      case "Top Down Profile":
        rowProperty = topDownProfileData[rowIndex]?.property;
        break;
      default:
        rowProperty = null;
    }

    // Prevent editing for "Orientation Width" and "Orientation Height"
    if (rowProperty === "Orientation Width" || rowProperty === "Orientation Height") {
      return; // Do nothing, prevent editing
    }

    if (columnId !== "property") {
      setEditableCell({rowIndex, columnId});

      // Set the temporary value depending on the current tab
      switch (selectedTab) {
        case "General Properties":
          setTemporaryValue(generalData[rowIndex][columnId]);
          break;
        case "Front Profile":
          setTemporaryValue(frontProfileData[rowIndex][columnId]);
          break;
        case "Side Profile":
          setTemporaryValue(sideProfileData[rowIndex][columnId]);
          break;
        case "Rear Profile":
          setTemporaryValue(rearProfileData[rowIndex][columnId]);
          break;
        case "Top Down Profile":
          setTemporaryValue(topDownProfileData[rowIndex][columnId]);
          break;
        default:
          break;
      }
    }
  };

  const numericFields = [
    "Width",
    "Height",
    "Length",
    "Orientation Width",
    "Orientation Height",
    "Radar Cross Section",
    "Retroreflectivity",
  ];
  
  /**
  * @description This function handles the change event for the editable cell.
  * It updates the temporary value and performs validation.
  * If the input is numeric and empty, it sets the value to null.
  *   
  * @param e - The change event from the input field.
  * @param rowProperty - The property name of the row being edited.
  * @returns void
  */
  const handleCellChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, rowProperty: string) => {
    const newValue = e.target.value;

    const isNumericField = numericFields.includes(rowProperty);

    // Check for basic validation first
    const errorMessage = validateInput(newValue, rowProperty);

    let processedValue: string | number | null = newValue
    if (isNumericField && newValue === "") {
      processedValue = null
    }

    // If either validation or comparison fails, show error message
    if (errorMessage) {
      setTemporaryValue(processedValue);
      setValidationErrors((prev) => ({
        ...prev,
        [`row-${editableCell?.rowIndex}`]: errorMessage,
      }));
    } else {
      // If all checks pass, update the temporary value
      setTemporaryValue(processedValue);
      setValidationErrors((prev) => ({
        ...prev,
        [`row-${editableCell?.rowIndex}`]: null, // Clear error
      }));
    }
  };

/**
 * @description This function is called when the user finishes editing a cell.
 * Handles the blur event for the editable cell.
 * Validates the input value and updates the corresponding data based on the selected tab.
 * If validation fails, it reverts to the previous valid value.
 * @returns void
 */
  const handleCellBlur = () => {
    if (editableCell) {
      const rowIndex = editableCell.rowIndex;
      const columnId = editableCell.columnId;

      // Get the property name based on the selected tab
      const propertyName = (() => {
        switch (selectedTab) {
          case "General Properties":
            return generalData[rowIndex]?.property;
          case "Front Profile":
            return frontProfileData[rowIndex]?.property;
          case "Side Profile":
            return sideProfileData[rowIndex]?.property;
          case "Rear Profile":
            return rearProfileData[rowIndex]?.property;
          case "Top Down Profile":
            return topDownProfileData[rowIndex]?.property;
          default:
            return null;
        }
      })();

      const finalErrorMessage = validateInput(temporaryValue, propertyName);

      if (!finalErrorMessage) {
        const isNumericField = numericFields.includes(propertyName);
        let processedValue: string | number | null = temporaryValue
        if (isNumericField && !isNaN(Number(temporaryValue))) {
          processedValue = Number(temporaryValue);
        }
        const updateData = (data: any[]) => {
          return data.map((row, index) => {
            if (index === rowIndex) {
              return {...row, [columnId]: processedValue};
            }
            return row;
          });
        };

        let updatedGeneralData = generalData;
        let updatedFrontProfileData = frontProfileData;
        let updatedSideProfileData = sideProfileData;
        let updatedRearProfileData = rearProfileData;
        let updatedTopDownProfileData = topDownProfileData;

        switch (selectedTab) {
          case "General Properties":
            updatedGeneralData = updateData(generalData);
            setGeneralData(updatedGeneralData);
            break;
          case "Front Profile":
            updatedFrontProfileData = updateData(frontProfileData);
            setFrontProfileData(updatedFrontProfileData);
            break;
          case "Side Profile":
            updatedSideProfileData = updateData(sideProfileData);
            setSideProfileData(updatedSideProfileData);
            break;
          case "Rear Profile":
            updatedRearProfileData = updateData(rearProfileData);
            setRearProfileData(updatedRearProfileData);
            break;
          case "Top Down Profile":
            updatedTopDownProfileData = updateData(topDownProfileData);
            setTopDownProfileData(updatedTopDownProfileData);
            break;
          default:
            break;
        }

        setValidationErrors((prev) => ({
          ...prev,
          [`row-${rowIndex}`]: null,
        }));
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          [`row-${rowIndex}`]: finalErrorMessage,
        }));
      }

      setEditableCell(null);
    }
  };


  const commonColumns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "property",
      header: "Property",
    },
    {
      accessorKey: "value",
      header: "Value",
      Cell: ({cell, row}: any) => (
        editableCell?.rowIndex === row.index && editableCell?.columnId === "value" ? (
          <TextField
            value={temporaryValue} // Use temporary value
            onChange={(e) => handleCellChange(e, row.original.property)} // Pass property name
            onBlur={handleCellBlur}
            error={!!validationErrors[`row-${row.index}`]} // Check for error
            helperText={validationErrors[`row-${row.index}`] || ""}
            autoFocus
          />
        ) : (
          <div
            onClick={() => handleCellClick(row.index, "value")}
            style={{cursor: "pointer", minWidth: "40px", padding: "4px"}}
          >
            {cell.getValue()}
          </div>
        )
      ),
      size: 300,
    },
    {
      accessorKey: "units",
      header: "Units",
    },
  ];

  return (
    <div style={{padding: "5px"}}>
      <Tabs value={selectedTab} onChange={handleTabChange}>
        <Tab label="General Properties" value="General Properties" />
        <Tab label="Front Profile" value="Front Profile" />
        <Tab label="Side Profile" value="Side Profile" />
        <Tab label="Rear Profile" value="Rear Profile" />
        <Tab label="Top Down Profile" value="Top Down Profile"/>
      </Tabs>
      <TabPanel value={selectedTab} name="General Properties">
        <ArgosTableSimple
          enableTopToolbar={false}
          columns={commonColumns}
          data={generalData}
          initialState={{density: "compact"}}
          enableHiding={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </TabPanel>
      <TabPanel value={selectedTab} name="Front Profile">
        <ArgosTableSimple
          enableTopToolbar={false}
          columns={commonColumns}
          data={frontProfileData}
          initialState={{density: "compact"}}
          enableHiding={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </TabPanel>
      <TabPanel value={selectedTab} name="Side Profile">
        <ArgosTableSimple
          enableTopToolbar={false}
          columns={commonColumns}
          data={sideProfileData}
          initialState={{density: "compact"}}
          enableHiding={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </TabPanel>
      <TabPanel value={selectedTab} name="Rear Profile">
        <ArgosTableSimple
          enableTopToolbar={false}
          columns={commonColumns}
          data={rearProfileData}
          initialState={{density: "compact"}}
          enableHiding={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </TabPanel>
      <TabPanel value={selectedTab} name="Top Down Profile">
        <ArgosTableSimple
          enableTopToolbar={false}
          columns={commonColumns}
          data={topDownProfileData}
          initialState={{density: "compact"}}
          enableHiding={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </TabPanel>
    </div>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  name: string;
  value: string;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, name, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== name}
      id={`full-width-tabpanel-${name}`}
      aria-labelledby={`full-width-tab-${name}`}
      {...other}
    >
      {value === name && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

export default MiscellaneousParameterProperties;
